import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { truncate } from 'lodash'
import { IconClock, IconCalendarCog } from '@tabler/icons-react'
import { format, differenceInDays } from 'date-fns'
import moment from 'moment'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { PageHeader } from '@/components/headers/PageHeader'
import { formatTime, getOrganizationOptions } from '@/utils/helpers'
import { fetchAudioIngestionRecords } from '@/reducers/audioIngestion/audioIngestion.actions'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { Pill } from '@/components/pills/Pill'
import { FilterTypeSelect } from '@/components/filters/types/FilterTypeSelect'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import * as redux from '@/reducers/audioIngestion/audioIngestion.redux'

import { statusMap, errorMap } from './audioIngestion.helpers'

export const AudioIngestionRecordsPage = () => {
  const dispatch = useDispatch()
  const {
    loading,
    audioIngestion: { records, filters, lastJobRunTime },
  } = useSelector((state) => state.audioIngestion)

  const { organizationid: currentUserOrgId } = useSelector((state) => state.currentUser)
  const { organizations } = useSelector((state) => state)
  const organizationOptions = getOrganizationOptions(organizations)
  const hasOrganizationDropdown = currentUserOrgId === 1

  const hasCsvFiles = records && records.some((row) => Boolean(row?.csv_filename))

  const columns = [
    {
      accessor: 'call_id',
      label: 'Call ID',
      isSearchable: true,
      isCopiable: true,
      collapsing: true,
      format: (value) => {
        if (!value) {
          return <span className="extra-muted-text">—</span>
        }

        return (
          <Link to={`/calls/${value}`} className="table-link">
            {value}
          </Link>
        )
      },
    },
    {
      accessor: 'voip_call_id',
      label: 'VoIP Call ID',
      isSearchable: true,
      isCopiable: true,
    },
    ...(!hasCsvFiles
      ? []
      : [
          {
            accessor: 'csv_filename',
            label: 'CSV File',
            isSearchable: true,
            format: (value) => {
              return (
                <InteractiveTooltip interactive content={value} hide={value?.length < 30}>
                  <strong>{truncate(value, { length: 30 })}</strong>
                </InteractiveTooltip>
              )
            },
          },
        ]),
    {
      accessor: 'ingestion_time',
      label: 'Ingestion Time',
      format: (value) => formatTime(value, 'MMM D, YYYY h:mm A'),
    },
    {
      accessor: 'status',
      label: 'Status',
      isSearchable: true,
      format: (value) => {
        const info = statusMap[value]

        if (!info) {
          return value
        }

        return (
          <InteractiveTooltip content={info.tooltip}>
            <Pill {...info.status} icon={info.icon} small>
              {info.label}
            </Pill>
          </InteractiveTooltip>
        )
      },
    },
    {
      accessor: 'error_detail',
      label: 'Error Detail',
      format: (value) => {
        return value ? errorMap[value] || value : <span className="extra-muted-text">—</span>
      },
    },
  ]

  const handleUpdateOrg = (_, selectedOrgId) => {
    dispatch(
      fetchAudioIngestionRecords({
        organizationId: selectedOrgId,
        dateInvalid: false,
      })
    )
  }

  const handleUpdateRange = (selection) => {
    const selectedStartDate = format(selection.startDate, "yyyy-MM-dd'T00:00:00'xxx")
    const selectedEndDate = format(selection.endDate, "yyyy-MM-dd'T23:59:59'xxx")

    const datesDiff = differenceInDays(selection.endDate, selection.startDate)

    if (datesDiff > 62) {
      dispatch(redux.setAudioIngestionRecords({ filters: { dateInvalid: true } }))
    }

    dispatch(
      fetchAudioIngestionRecords({
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        dateInvalid: false,
      })
    )
  }

  useEffect(() => {
    // Set the filters for org dropdown to current user's org
    dispatch(fetchAudioIngestionRecords({ organizationId: currentUserOrgId }))
  }, [])

  return (
    <>
      <PageHeader isDataGrid title="Audio Ingestion Records" data-testid="audio-ingestion-header" />
      <header className="data-grid-subheader">
        <div className="flex-align-center small-gap">
          {hasOrganizationDropdown && (
            <FilterTypeSelect
              isClearable={false}
              schema={{
                accessor: 'organizationId',
                label: 'Organization',
                options: organizationOptions,
              }}
              updateSearch={handleUpdateOrg}
              value={filters.organizationId}
            />
          )}
          <DateRangePickerReusable
            data-testid="filter-type-date-range"
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={({ selection }) => handleUpdateRange(selection)}
            shouldNotResetOnCancel
            error={filters.dateInvalid}
            disableSaveButton={filters.dateInvalid}
            friendlyLabel
            basicLabel
            isFilterPill
            icon={<IconCalendarCog />}
            saveOrCloseOnClickPreset
          />
        </div>
        {lastJobRunTime && (
          <div className="flex-align-center small-gap" data-testid="last-job-run-time">
            <IconClock className="status-muted icon-svg" />
            <span>Last job:</span>
            <strong>{formatTime(lastJobRunTime, 'MMM D, YYYY h:mm A')}</strong>
            <span className="muted-text">{`(${moment(lastJobRunTime).fromNow()})`}</span>
          </div>
        )}
      </header>
      <AdvancedTable
        dataGrid
        columns={columns}
        rows={records}
        loading={loading.records}
        noDataHeader="No records found"
        noDataText="Try increasing the date range or modifying your current filters."
        rowsPerPage={30}
        pagination
        index="call_id"
        defaultOrder="desc"
        defaultOrderBy="start_time"
      />
    </>
  )
}
